import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Mail, Trash2, Pencil, Save, X, Plus, RefreshCcw, Upload, ImagePlus } from "lucide-react";
import { fullImageUrl } from "@/lib/images";

const TABS = [
  { key: "enquiries", label: "Enquiries" },
  { key: "images", label: "Photographs" },
  { key: "testimonials", label: "Testimonials" },
  { key: "amenities", label: "Amenities" },
  { key: "faqs", label: "FAQs" },
  { key: "policies", label: "Policies" },
  { key: "contact", label: "Contact info" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ enquiries: [], testimonials: [], amenities: [], faqs: [], contact: {}, images: [], policies: [] });

  const logout = useCallback(() => {
    localStorage.removeItem("chimano_token");
    localStorage.removeItem("chimano_admin");
    navigate("/admin/login");
  }, [navigate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [enq, content, imgs, pols] = await Promise.all([
        api.get("/admin/enquiries"),
        api.get("/content"),
        api.get("/admin/images"),
        api.get("/admin/policies"),
      ]);
      setData({
        enquiries: enq.data || [],
        testimonials: content.data.testimonials || [],
        amenities: content.data.amenities || [],
        faqs: content.data.faqs || [],
        contact: content.data.contact || {},
        images: imgs.data || [],
        policies: pols.data || [],
      });
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        return;
      }
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (!localStorage.getItem("chimano_token")) {
      navigate("/admin/login");
      return;
    }
    void fetchAll();
  }, [fetchAll, navigate]);

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <header className="border-b border-[#2C392F]/10 bg-[#F7F5F0]/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-2" data-testid="admin-home-link">
            <span className="serif text-xl text-[#2C392F]">ChiMano</span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#985F47]">Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh"
              onClick={fetchAll}
              className="p-2 rounded-full hover:bg-[#EFEAE4]"
              aria-label="Refresh"
            >
              <RefreshCcw size={16} className="text-[#2C392F]" />
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-[#2C392F] border border-[#2C392F]/30 hover:bg-[#2C392F] hover:text-[#F7F5F0] px-4 py-2 rounded-full transition-all"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-10 flex gap-1 overflow-x-auto border-t border-[#2C392F]/5">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-3 text-[12px] uppercase tracking-[0.18em] transition-colors border-b-2 ${
                tab === t.key
                  ? "text-[#985F47] border-[#985F47]"
                  : "text-[#4A4D4A] border-transparent hover:text-[#2C392F]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {loading ? (
          <div className="text-center text-[#4A4D4A] py-20">Loading…</div>
        ) : (
          <>
            {tab === "enquiries" && <EnquiriesTab items={data.enquiries} refresh={fetchAll} />}
            {tab === "images" && <ImagesTab items={data.images} refresh={fetchAll} />}
            {tab === "testimonials" && <TestimonialsTab items={data.testimonials} refresh={fetchAll} />}
            {tab === "amenities" && <AmenitiesTab items={data.amenities} refresh={fetchAll} />}
            {tab === "faqs" && <FAQsTab items={data.faqs} refresh={fetchAll} />}
            {tab === "policies" && <PoliciesTab items={data.policies} refresh={fetchAll} />}
            {tab === "contact" && <ContactTab contact={data.contact} refresh={fetchAll} />}
          </>
        )}
      </main>
    </div>
  );
}

// --------------- Enquiries ---------------
function EnquiriesTab({ items, refresh }) {
  const [openId, setOpenId] = useState(null);

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await api.delete(`/admin/enquiries/${id}`);
      toast.success("Enquiry deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (!items.length)
    return <div className="text-[#4A4D4A] serif text-2xl py-16 text-center">No enquiries yet.</div>;

  return (
    <div className="space-y-3">
      {items.map((it) => (
        <div
          key={it.id}
          data-testid={`enquiry-row-${it.id}`}
          className="bg-[#FBF9F4] border border-[#2C392F]/10 rounded-2xl p-6"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="serif text-xl text-[#2C392F]">{it.full_name}</div>
              <div className="text-sm text-[#4A4D4A] mt-1">
                {it.email} · {it.phone}
              </div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#985F47] mt-2">
                {new Date(it.created_at).toLocaleString()} · {it.stay_type} stay · {it.adults} adults
                {it.children ? `, ${it.children} children` : ""}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={`mailto:${it.email}`}
                className="p-2 rounded-full hover:bg-[#EFEAE4]"
                aria-label="Email"
              >
                <Mail size={16} className="text-[#2C392F]" />
              </a>
              <button
                onClick={() => setOpenId(openId === it.id ? null : it.id)}
                className="text-[12px] uppercase tracking-[0.2em] text-[#2C392F] border border-[#2C392F]/30 hover:bg-[#2C392F] hover:text-[#F7F5F0] px-3 py-1.5 rounded-full transition-all"
              >
                {openId === it.id ? "Hide" : "Details"}
              </button>
              <button
                onClick={() => remove(it.id)}
                className="p-2 rounded-full hover:bg-[#EFEAE4]"
                aria-label="Delete"
              >
                <Trash2 size={16} className="text-[#985F47]" />
              </button>
            </div>
          </div>

          {openId === it.id && (
            <div className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <Detail k="Check-in" v={it.check_in || "—"} />
              <Detail k="Check-out" v={it.check_out || "—"} />
              <Detail k="Flexible dates" v={it.flexible_dates ? "Yes" : "No"} />
              <Detail k="Purpose" v={it.purpose || "—"} />
              <Detail k="Meal requirements" v={it.meal_requirements || "—"} />
              <Detail k="Special requirements" v={it.special_requirements || "—"} />
              <Detail k="Consent" v={it.consent ? "Yes" : "No"} />
              <Detail k="Message" v={it.message || "—"} full />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
const Detail = ({ k, v, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#985F47]">{k}</div>
    <div className="text-[#1A1C1A] mt-1 whitespace-pre-wrap">{v}</div>
  </div>
);

// --------------- Testimonials ---------------
function TestimonialsTab({ items, refresh }) {
  const empty = { quote: "", guest_name: "", city: "", is_placeholder: false, order: items.length + 1 };
  return (
    <CRUDList
      items={items}
      empty={empty}
      endpoint="/admin/testimonials"
      refresh={refresh}
      render={(it, setVal) => (
        <>
          <Input label="Quote" value={it.quote} onChange={(v) => setVal("quote", v)} multiline />
          <Input label="Guest name" value={it.guest_name} onChange={(v) => setVal("guest_name", v)} />
          <Input label="City" value={it.city} onChange={(v) => setVal("city", v)} />
          <Input
            label="Order"
            type="number"
            value={it.order}
            onChange={(v) => setVal("order", Number(v) || 0)}
          />
          <Checkbox
            label="Mark as placeholder"
            value={it.is_placeholder}
            onChange={(v) => setVal("is_placeholder", v)}
          />
        </>
      )}
      summary={(it) => ({ title: it.guest_name || "Untitled", subtitle: it.quote })}
    />
  );
}

// --------------- Amenities ---------------
function AmenitiesTab({ items, refresh }) {
  const empty = { label: "", icon: "Check", confirmed: true, order: items.length + 1 };
  return (
    <CRUDList
      items={items}
      empty={empty}
      endpoint="/admin/amenities"
      refresh={refresh}
      render={(it, setVal) => (
        <>
          <Input label="Label" value={it.label} onChange={(v) => setVal("label", v)} />
          <Input label="Icon (lucide-react name)" value={it.icon || ""} onChange={(v) => setVal("icon", v)} />
          <Input
            label="Order"
            type="number"
            value={it.order}
            onChange={(v) => setVal("order", Number(v) || 0)}
          />
          <Checkbox
            label="Confirmed amenity"
            value={it.confirmed}
            onChange={(v) => setVal("confirmed", v)}
          />
        </>
      )}
      summary={(it) => ({ title: it.label, subtitle: it.icon })}
    />
  );
}

// --------------- FAQs ---------------
function FAQsTab({ items, refresh }) {
  const empty = { question: "", answer: "", order: items.length + 1 };
  return (
    <CRUDList
      items={items}
      empty={empty}
      endpoint="/admin/faqs"
      refresh={refresh}
      render={(it, setVal) => (
        <>
          <Input label="Question" value={it.question} onChange={(v) => setVal("question", v)} />
          <Input label="Answer" value={it.answer} onChange={(v) => setVal("answer", v)} multiline />
          <Input
            label="Order"
            type="number"
            value={it.order}
            onChange={(v) => setVal("order", Number(v) || 0)}
          />
        </>
      )}
      summary={(it) => ({ title: it.question, subtitle: it.answer })}
    />
  );
}

// --------------- Contact ---------------
function ContactTab({ contact, refresh }) {
  const [form, setForm] = useState({ ...contact });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/contact", form);
      toast.success("Contact info saved");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#FBF9F4] border border-[#2C392F]/10 rounded-2xl p-8 max-w-3xl">
      <div className="serif text-2xl text-[#2C392F]">Contact details</div>
      <p className="text-sm text-[#4A4D4A] mt-1">
        These are shown publicly across the website.
      </p>
      <div className="mt-8 space-y-6">
        <Input label="Phone" value={form.phone || ""} onChange={(v) => set("phone", v)} />
        <Input label="Email" value={form.email || ""} onChange={(v) => set("email", v)} />
        <Input
          label="WhatsApp (digits only for click-to-chat)"
          value={form.whatsapp || ""}
          onChange={(v) => set("whatsapp", v)}
        />
        <Input label="Address" value={form.address || ""} onChange={(v) => set("address", v)} />
        <Input label="Instagram handle" value={form.instagram || ""} onChange={(v) => set("instagram", v)} />
        <Input
          label="Maps embed URL (optional)"
          value={form.maps_embed_url || ""}
          onChange={(v) => set("maps_embed_url", v)}
        />
      </div>
      <button
        data-testid="admin-contact-save"
        onClick={save}
        disabled={saving}
        className="mt-8 inline-flex items-center gap-2 bg-[#2C392F] text-[#F7F5F0] hover:bg-[#1E2823] disabled:opacity-60 px-6 py-3 rounded-full text-[12px] tracking-[0.2em] uppercase"
      >
        {saving ? "Saving…" : "Save"} <Save size={14} />
      </button>
    </div>
  );
}

// --------------- Shared CRUD list ---------------
function CRUDList({ items, empty, endpoint, refresh, render, summary }) {
  const [editing, setEditing] = useState(null); // id or "new"
  const [draft, setDraft] = useState(null);

  const start = (it) => {
    setEditing(it ? it.id : "new");
    setDraft(it ? { ...it } : { ...empty });
  };

  const cancel = () => {
    setEditing(null);
    setDraft(null);
  };

  const setVal = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    try {
      if (editing === "new") {
        await api.post(endpoint, draft);
        toast.success("Created");
      } else {
        await api.put(`${endpoint}/${editing}`, draft);
        toast.success("Updated");
      }
      cancel();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="text-sm text-[#4A4D4A]">{items.length} item(s)</div>
        <button
          data-testid="admin-add-new"
          onClick={() => start(null)}
          className="inline-flex items-center gap-2 bg-[#985F47] hover:bg-[#86513A] text-[#F7F5F0] px-4 py-2 rounded-full text-[12px] uppercase tracking-[0.18em]"
        >
          <Plus size={14} /> Add new
        </button>
      </div>

      {editing && (
        <div className="bg-[#FBF9F4] border border-[#985F47]/30 rounded-2xl p-6 mb-5 space-y-4">
          {render(draft, setVal)}
          <div className="flex gap-3 pt-2">
            <button
              data-testid="admin-save-item"
              onClick={save}
              className="inline-flex items-center gap-2 bg-[#2C392F] text-[#F7F5F0] hover:bg-[#1E2823] px-5 py-2.5 rounded-full text-[12px] tracking-[0.18em] uppercase"
            >
              <Save size={14} /> Save
            </button>
            <button
              onClick={cancel}
              className="inline-flex items-center gap-2 border border-[#2C392F]/30 text-[#2C392F] hover:bg-[#EFEAE4] px-5 py-2.5 rounded-full text-[12px] tracking-[0.18em] uppercase"
            >
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.map((it) => {
          const s = summary(it);
          return (
            <div
              key={it.id}
              className="bg-[#FBF9F4] border border-[#2C392F]/10 rounded-2xl p-5 flex items-start gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="serif text-lg text-[#2C392F] line-clamp-1">{s.title}</div>
                <div className="text-sm text-[#4A4D4A] line-clamp-2 mt-1">{s.subtitle}</div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => start(it)} className="p-2 rounded-full hover:bg-[#EFEAE4]" aria-label="Edit">
                  <Pencil size={16} className="text-[#2C392F]" />
                </button>
                <button onClick={() => remove(it.id)} className="p-2 rounded-full hover:bg-[#EFEAE4]" aria-label="Delete">
                  <Trash2 size={16} className="text-[#985F47]" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", multiline = false }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-[#985F47] mb-2">{label}</label>
      {multiline ? (
        <textarea
          rows={3}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent border-b border-[#2C392F]/25 focus:border-[#2C392F] outline-none px-0 py-2 text-[15px] resize-none"
        />
      ) : (
        <input
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent border-b border-[#2C392F]/25 focus:border-[#2C392F] outline-none px-0 py-2 text-[15px]"
        />
      )}
    </div>
  );
}

function Checkbox({ label, value, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-[#1A1C1A]">
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-[#2C392F]"
      />
      {label}
    </label>
  );
}


// --------------- Images / Photographs ---------------
const PLACEMENT_LABELS = {
  hero: "Hero photograph (top of landing page)",
  intro: "Introduction section image",
  mussoorie: "Mussoorie section (six numbered slots)",
  experience: "The ChiMano Experience (up to 3)",
  food: "Food & caretaker section",
  gallery: "Photo gallery",
};
const PLACEMENTS = ["hero", "intro", "mussoorie", "experience", "food", "gallery"];
const GALLERY_CATEGORIES = ["Property", "Gardens", "Interiors", "Care"];

// Maps the `order` number to the labelled slot in the Mussoorie section, so the
// admin sees exactly which photograph each upload will become on the site.
const MUSSOORIE_SLOTS = {
  1: "Daytime view towards Mussoorie",
  2: "Evening lights of Mussoorie",
  3: "Open compound with hill backdrop",
  4: "Outdoor seating facing the hills",
  5: "Driveway or approach to ChiMano House",
  6: "Garden and orchard with distant hills",
};

function ImagesTab({ items, refresh }) {
  const [file, setFile] = useState(null);
  const [placement, setPlacement] = useState("gallery");
  const [category, setCategory] = useState("Property");
  const [caption, setCaption] = useState("");
  const [alt, setAlt] = useState("");
  const [order, setOrder] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setFile(null);
    setCaption("");
    setAlt("");
    setOrder(0);
    if (fileRef.current) fileRef.current.value = "";
  };

  const upload = async () => {
    if (!file) {
      toast.error("Please choose an image first");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("placement", placement);
      if (placement === "gallery") fd.append("category", category);
      fd.append("caption", caption);
      fd.append("alt", alt);
      fd.append("order", String(order));
      await api.post("/admin/images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photograph uploaded");
      reset();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this photograph? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/images/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const grouped = PLACEMENTS.reduce((acc, p) => {
    acc[p] = items.filter((i) => i.placement === p).sort((a, b) => (a.order || 0) - (b.order || 0));
    return acc;
  }, {});

  return (
    <div className="space-y-10">
      {/* Upload form */}
      <div
        data-testid="image-upload-card"
        className="bg-[#FBF9F4] border border-[#985F47]/30 rounded-2xl p-6 md:p-8"
      >
        <div className="serif text-2xl text-[#2C392F]">Upload a photograph</div>
        <p className="text-sm text-[#4A4D4A] mt-1">
          Choose the section where the photograph should appear. For the Gallery, also pick a category.
        </p>

        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.3em] text-[#985F47] mb-2">Placement</label>
            <select
              data-testid="image-placement-select"
              value={placement}
              onChange={(e) => setPlacement(e.target.value)}
              className="w-full bg-transparent border-b border-[#2C392F]/25 focus:border-[#2C392F] outline-none px-0 py-2 text-[15px]"
            >
              {PLACEMENTS.map((p) => (
                <option key={p} value={p}>
                  {PLACEMENT_LABELS[p]}
                </option>
              ))}
            </select>
          </div>

          {placement === "gallery" && (
            <div>
              <label className="block text-[10px] uppercase tracking-[0.3em] text-[#985F47] mb-2">Gallery category</label>
              <select
                data-testid="image-category-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-transparent border-b border-[#2C392F]/25 focus:border-[#2C392F] outline-none px-0 py-2 text-[15px]"
              >
                {GALLERY_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          )}

          {placement === "mussoorie" && (
            <div className="md:col-span-2 bg-[#F7F5F0] border border-[#2C392F]/10 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#985F47] mb-2">
                Mussoorie slot — pick by Order number
              </div>
              <ul className="text-sm text-[#1A1C1A] grid sm:grid-cols-2 gap-x-6 gap-y-1">
                {Object.entries(MUSSOORIE_SLOTS).map(([num, label]) => (
                  <li key={num} className="flex gap-2">
                    <span className="serif text-[#985F47] tabular-nums w-5">{num}.</span>
                    <span className="text-[#4A4D4A]">{label}</span>
                  </li>
                ))}
              </ul>
              {order >= 1 && order <= 6 && (
                <div className="mt-3 text-[12px] italic text-[#2C392F]">
                  This upload will appear as: <span className="serif font-medium">{MUSSOORIE_SLOTS[order]}</span>
                </div>
              )}
            </div>
          )}

          <Input label="Caption (optional)" value={caption} onChange={setCaption} />
          <Input label="Alt text for accessibility" value={alt} onChange={setAlt} />
          <Input
            label={placement === "mussoorie" ? "Order (1–6 = which Mussoorie slot)" : "Order (lower = first)"}
            type="number"
            value={order}
            onChange={(v) => setOrder(Number(v) || 0)}
          />

          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.3em] text-[#985F47] mb-2">Image file</label>
            <input
              ref={fileRef}
              data-testid="image-file-input"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-[#1A1C1A] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#2C392F] file:text-[#F7F5F0] hover:file:bg-[#1E2823] file:cursor-pointer file:text-[11px] file:uppercase file:tracking-[0.18em]"
            />
            {file && (
              <div className="mt-2 text-xs text-[#4A4D4A]">
                Selected: {file.name} · {(file.size / 1024).toFixed(0)} KB
              </div>
            )}
          </div>
        </div>

        <button
          data-testid="image-upload-submit"
          onClick={upload}
          disabled={uploading || !file}
          className="mt-7 inline-flex items-center gap-2 bg-[#985F47] hover:bg-[#86513A] disabled:opacity-60 text-[#F7F5F0] px-6 py-3 rounded-full text-[12px] tracking-[0.2em] uppercase transition-all"
        >
          {uploading ? "Uploading…" : "Upload photograph"} <Upload size={14} />
        </button>
      </div>

      {/* Grouped listing */}
      {PLACEMENTS.map((p) => {
        const list = grouped[p];
        return (
          <div key={p}>
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="serif text-2xl text-[#2C392F]">{PLACEMENT_LABELS[p]}</h3>
              <span className="text-[11px] uppercase tracking-[0.25em] text-[#985F47]">{list.length} image(s)</span>
            </div>
            {list.length === 0 ? (
              <div className="text-sm text-[#4A4D4A] italic bg-[#FBF9F4] border border-dashed border-[#2C392F]/20 rounded-2xl p-6">
                No photographs yet — the design placeholder will be shown on the landing page until you upload one.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {list.map((img) => (
                  <div
                    key={img.id}
                    data-testid={`image-card-${img.id}`}
                    className="bg-[#FBF9F4] border border-[#2C392F]/10 rounded-2xl overflow-hidden flex flex-col"
                  >
                    <div className="relative aspect-[4/3] bg-[#EFEAE4]">
                      <img
                        src={fullImageUrl(img)}
                        alt={img.alt || img.caption || "ChiMano photograph"}
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-[#985F47]">
                        {img.category ? `${img.placement} · ${img.category}` : img.placement} · #{img.order}
                      </div>
                      <div className="serif text-base text-[#2C392F] mt-1 line-clamp-2">
                        {img.caption || "—"}
                      </div>
                      <button
                        onClick={() => remove(img.id)}
                        className="mt-3 self-start inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-[#985F47] hover:text-[#86513A]"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Hint */}
      <div className="bg-[#EFEAE4] border border-[#2C392F]/10 rounded-2xl p-5 flex items-start gap-3 text-sm text-[#4A4D4A]">
        <ImagePlus className="text-[#985F47] mt-0.5" size={18} />
        <div>
          Use landscape photographs for the hero, intro, food and experience sections (around 1600×1200 or larger). JPEG/PNG/WebP up to 8&nbsp;MB.
        </div>
      </div>
    </div>
  );
}

// --------------- Policies (Privacy / Terms / Cancellation) ---------------
const POLICY_LABELS = {
  privacy: "Privacy policy",
  terms: "Terms of stay",
  cancellation: "Cancellation policy",
};

function PoliciesTab({ items, refresh }) {
  const [activeSlug, setActiveSlug] = useState(items[0]?.slug || "privacy");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  // When tab or items change, load the editable draft from the currently selected policy.
  useEffect(() => {
    const found = items.find((p) => p.slug === activeSlug);
    setDraft(
      found
        ? { ...found }
        : { slug: activeSlug, title: POLICY_LABELS[activeSlug] || activeSlug, last_updated: "", content: "" }
    );
  }, [activeSlug, items]);

  if (!draft) return null;

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/policies/${draft.slug}`, draft);
      toast.success("Policy saved");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const slugs = ["privacy", "terms", "cancellation"];

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <aside className="lg:col-span-3 space-y-1">
        {slugs.map((s) => (
          <button
            key={s}
            data-testid={`policy-select-${s}`}
            onClick={() => setActiveSlug(s)}
            className={`block w-full text-left px-4 py-3 rounded-xl text-sm transition-all border ${
              activeSlug === s
                ? "bg-[#2C392F] text-[#F7F5F0] border-[#2C392F]"
                : "bg-[#FBF9F4] text-[#2C392F] border-[#2C392F]/10 hover:border-[#985F47]/40"
            }`}
          >
            <div className="serif text-base leading-tight">{POLICY_LABELS[s]}</div>
            <div className={`text-[10px] uppercase tracking-[0.2em] mt-1 ${activeSlug === s ? "text-[#D89B7E]" : "text-[#985F47]"}`}>
              /{s === "privacy" ? "privacy-policy" : s === "terms" ? "terms-of-stay" : "cancellation-policy"}
            </div>
          </button>
        ))}

        <div className="mt-6 bg-[#EFEAE4] border border-[#2C392F]/10 rounded-xl p-4 text-xs text-[#4A4D4A] leading-relaxed">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#985F47] mb-2">Formatting tips</div>
          <div>Start a line with <code className="bg-[#F7F5F0] px-1 rounded">## </code> for a heading.</div>
          <div className="mt-1">Start a line with <code className="bg-[#F7F5F0] px-1 rounded">- </code> for a bullet.</div>
          <div className="mt-1">Wrap text in <code className="bg-[#F7F5F0] px-1 rounded">**…**</code> for <strong>bold</strong>.</div>
          <div className="mt-1">Leave a blank line between paragraphs.</div>
        </div>
      </aside>

      <div className="lg:col-span-9 bg-[#FBF9F4] border border-[#2C392F]/10 rounded-2xl p-6 md:p-8">
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.3em] text-[#985F47] mb-2">Title</label>
            <input
              data-testid="policy-title-input"
              value={draft.title || ""}
              onChange={(e) => set("title", e.target.value)}
              className="w-full bg-transparent border-b border-[#2C392F]/25 focus:border-[#2C392F] outline-none px-0 py-2 text-[15px]"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.3em] text-[#985F47] mb-2">Last updated</label>
            <input
              data-testid="policy-updated-input"
              value={draft.last_updated || ""}
              onChange={(e) => set("last_updated", e.target.value)}
              placeholder="e.g. January 2027"
              className="w-full bg-transparent border-b border-[#2C392F]/25 focus:border-[#2C392F] outline-none px-0 py-2 text-[15px]"
            />
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-[10px] uppercase tracking-[0.3em] text-[#985F47] mb-2">Content</label>
          <textarea
            data-testid="policy-content-input"
            value={draft.content || ""}
            onChange={(e) => set("content", e.target.value)}
            rows={22}
            className="w-full bg-[#F7F5F0] border border-[#2C392F]/15 focus:border-[#2C392F] outline-none rounded-xl px-4 py-3 text-[14px] leading-relaxed font-mono"
            placeholder="## Section heading&#10;&#10;Paragraph text here.&#10;&#10;- Bullet point one&#10;- Bullet point two&#10;&#10;**Bold text** appears like this."
          />
        </div>

        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          <a
            href={
              draft.slug === "privacy"
                ? "/privacy-policy"
                : draft.slug === "terms"
                ? "/terms-of-stay"
                : "/cancellation-policy"
            }
            target="_blank"
            rel="noreferrer"
            data-testid="policy-preview-link"
            className="text-[12px] uppercase tracking-[0.2em] text-[#985F47] hover:text-[#86513A] inline-flex items-center gap-1"
          >
            Preview live page ↗
          </a>
          <button
            data-testid="policy-save-button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#2C392F] text-[#F7F5F0] hover:bg-[#1E2823] disabled:opacity-60 px-6 py-3 rounded-full text-[12px] tracking-[0.2em] uppercase transition-all"
          >
            {saving ? "Saving…" : "Save policy"} <Save size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

